import React, { useEffect, useState } from "react";
import { observer } from "mobx-react";
import { Box, Grid2, Paper, useTheme } from "@mui/material";
import { useSearchParams } from "react-router-dom";
import { useStores } from "../../../use-stores";
import { EmployeeRoles } from "../../../../../models/Employees/EmployeeRoles";
import PermissionBlockLabel from "../../../../../views/components/common/pop-up/PermissionBlockLabel ";
import LoaderOverlay from "../../../../../views/components/common/pages/AppLoader";
import CustomTypography from "../../../../../views/components/common/form/CustomTypography";
import {
  AlertsDashboardStore,
  AlertsDashboardTab,
} from "../../../stores/dashboards/AlertsDashboardStore";
import AlertsFilters from "../../components/dashboard/alerts/AlertsFilters";
import AlertsKpiCards from "../../components/dashboard/alerts/AlertsKpiCards";
import AlertsTrendChart from "../../components/dashboard/alerts/AlertsTrendChart";
import AlertsBreakdownTables from "../../components/dashboard/alerts/AlertsBreakdownTables";
import AlertsListPanel from "../../components/dashboard/alerts/AlertsListPanel";
import AlertsDrawer from "../../components/dashboard/alerts/AlertsDrawer";
import { AlertsToggleRow } from "../../components/dashboard/alerts/AlertsDetailParts";
import RedemptionHint from "../../components/dashboard/redemption/RedemptionHint";

const TAB_IDS: AlertsDashboardTab[] = ["overview", "emails", "push"];

const parseTab = (raw: string | null): AlertsDashboardTab =>
  TAB_IDS.includes(raw as AlertsDashboardTab)
    ? (raw as AlertsDashboardTab)
    : "overview";

const AlertsDashboardView: React.FC = observer(() => {
  const theme = useTheme();
  const colors = theme.palette.customColors;
  const [store] = useState(() => new AlertsDashboardStore());
  const [searchParams, setSearchParams] = useSearchParams();
  const audience =
    store.summaryResult?.meta.audience ?? store.appliedFilters.audience;

  useEffect(() => {
    // Runs after AlertsFilters has applied the URL filters (child effects run first).
    const initialTab = parseTab(searchParams.get("tab"));
    if (initialTab !== "overview") {
      void store.changeTab(initialTab);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleTab = (tab: AlertsDashboardTab) => {
    try {
      void store.changeTab(tab);
      setSearchParams(
        (current) => {
          const next = new URLSearchParams(current);
          if (tab === "overview") {
            next.delete("tab");
          } else {
            next.set("tab", tab);
          }
          return next;
        },
        { replace: true },
      );
    } catch (error) {
      console.error(error);
    }
  };

  const tabs: Array<{ id: AlertsDashboardTab; label: string }> = [
    { id: "overview", label: "Overview" },
  ];
  if (audience !== "ADMIN" || store.tab === "emails") {
    tabs.push({ id: "emails", label: "Emails" });
  }
  if (audience !== "CUSTOMER" || store.tab === "push") {
    tabs.push({ id: "push", label: "Push" });
  }

  const list = store.tabList;

  return (
    <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 12 }} sx={{ px: 2, pb: 4 }}>
      <LoaderOverlay loading={store.summaryLoading && !store.summaryResult} />
      <AlertsFilters store={store} />

      <Box sx={{ mt: 2 }}>
        <AlertsToggleRow
          options={tabs}
          value={store.tab}
          onChange={handleTab}
        />
      </Box>

      {store.tab === "overview" ? (
        <>
          <AlertsKpiCards store={store} />
          <AlertsTrendChart store={store} />
          <AlertsBreakdownTables store={store} />
        </>
      ) : list ? (
        <Paper
          sx={{
            mt: 2,
            mb: 4,
            p: { xs: 2, md: 3 },
            borderRadius: 2,
            boxShadow:
              "rgba(0, 0, 0, 0.15) 0px 4px 24px, rgba(0, 0, 0, 0.05) 0px 8px 32px",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 2 }}>
            <CustomTypography variant="heading5" color={colors.navyText}>
              {list.title}
            </CustomTypography>
            <RedemptionHint how={list.result?.data.how ?? null} />
          </Box>
          <AlertsListPanel
            key={list.id}
            store={store}
            view={list}
            maxHeight={window.innerHeight - 300}
          />
        </Paper>
      ) : null}

      <AlertsDrawer store={store} />
    </Grid2>
  );
});

const AlertsDashboard: React.FC = () => {
  const { userStore } = useStores();

  if (!userStore.hasPermission(EmployeeRoles.VIEW_ALERTS_DASHBOARD)) {
    return <PermissionBlockLabel />;
  }

  return <AlertsDashboardView />;
};

export default observer(AlertsDashboard);
